import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Chip,
    Button,
    IconButton,
    Alert,
    TextField,
    CircularProgress
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Code as CodeIcon,
    ContentCopy as CopyIcon,
    Check as CheckIcon
} from '@mui/icons-material';
import { chatService } from '../../services/ChatService';

export function APIIntegration(data: any) {

    const [copiedStates, setCopiedStates] = useState<{ [key: string]: boolean }>({});
    const [apiMethod, setApiMethod] = useState('POST');
    const [apiBody, setApiBody] = useState('{\n  "model": "model-name",\n  "messages": [\n    {\n      "role": "user",\n      "content": "Hello, how are you?"\n    }\n  ]}');
    const [apiResponse, setApiResponse] = useState('');
    const [isLoadingApi, setIsLoadingApi] = useState(false);
    const [apiError, setApiError] = useState('');
    const [deployment, setDeployment] = useState(data.deployment);


    useEffect(() => {
        console.log('Deployment data:', deployment);
    }, [deployment]);
    const handleCopy = async (text: string, key: string) => {
        try {
            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                // Fallback for older browsers or when clipboard API is blocked
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                try {
                    document.execCommand('copy');
                } catch (err) {
                    console.error('Fallback copy failed:', err);
                }
                document.body.removeChild(textArea);
            }

            setCopiedStates({ ...copiedStates, [key]: true });
            setTimeout(() => {
                setCopiedStates({ ...copiedStates, [key]: false });
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
            // Still show feedback even if copy failed
            setCopiedStates({ ...copiedStates, [key]: true });
            setTimeout(() => {
                setCopiedStates({ ...copiedStates, [key]: false });
            }, 2000);
        }
    };

    const handleSendApiRequest = async () => {
        setIsLoadingApi(true);
        setApiError('');
        setApiResponse('');

        try {
            const parsed = JSON.parse(apiBody);            
            parsed['model'] = '/' + deployment.model_name;
            const newModel = await chatService.chatCompletions(parsed);
            const demoResponse = {
                id: 'chatcmpl-demo123',
                object: 'chat.completion',
                created: Math.floor(Date.now() / 1000),
                model: deployment?.modelVersion || 'model-name',
                choices: [
                    {
                        index: 0,
                        message: {
                            role: 'assistant',
                            content: newModel.choices[0].message.content
                        },
                        finish_reason: 'stop'
                    }
                ],
                usage: {
                    prompt_tokens: 12,
                    completion_tokens: 25,
                    total_tokens: 37
                }
            };
            setApiResponse(JSON.stringify(demoResponse, null, 2));
        } catch (error) {
            setApiError('Unable to connect to the API endpoint.');
        } finally {
            setIsLoadingApi(false);
        }
    };

    return (<Box sx={{ p: 4 }}>
        <Box sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: '#0f172a' }}>
                API Integration
            </Typography>
            <Typography variant="body2" color="text.secondary">
                Test and interact with the {deployment.model_name} deployment API directly
            </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
            {/* Left: Request Editor */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #e2e8f0',
                        borderRadius: 3,
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ p: 3, bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CodeIcon sx={{ color: '#667eea' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                Request
                            </Typography>
                        </Box>
                        <Chip label={apiMethod} size="small" sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 700 }} />
                    </Box>

                    <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, mb: 1, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}>
                            ENDPOINT
                        </Typography>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                bgcolor: '#fafbfc',
                                border: '1px solid #e2e8f0',
                                borderRadius: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#0f172a', fontSize: '13px', wordBreak: 'break-all' }}>
                                {deployment.end_point}
                            </Typography>
                        </Paper>
                    </Box>

                    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                                BODY
                            </Typography>
                            <IconButton size="small" onClick={() => handleCopy(apiBody, 'body')}>
                                {copiedStates['body'] ? <CheckIcon fontSize="small" sx={{ color: '#10b981' }} /> : <CopyIcon fontSize="small" />}
                            </IconButton>
                        </Box>
                        <TextField
                            multiline
                            rows={12}
                            value={apiBody}
                            onChange={(e) => setApiBody(e.target.value)}
                            variant="outlined"
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    bgcolor: '#fafbfc',
                                    '& fieldset': {
                                        borderColor: '#e2e8f0'
                                    }
                                }
                            }}
                        />
                    </Box>

                    <Box sx={{ p: 3, borderTop: '1px solid #e2e8f0' }}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="large"
                            onClick={handleSendApiRequest}
                            disabled={isLoadingApi}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                py: 1.5,
                                fontWeight: 700,
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #5568d3 0%, #6b3f94 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)'
                                },
                                transition: 'all 0.3s'
                            }}
                        >
                            {isLoadingApi ? 'Sending Request...' : 'Send Request'}
                        </Button>
                    </Box>
                </Paper>
            </Box>

            {/* Right: Response Viewer */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <Paper
                    elevation={0}
                    sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid #e2e8f0',
                        borderRadius: 3,
                        overflow: 'hidden'
                    }}
                >
                    <Box sx={{ p: 3, bgcolor: '#fafbfc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CheckCircleIcon sx={{ color: apiResponse ? '#10b981' : '#94a3b8' }} />
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a' }}>
                                Response
                            </Typography>
                        </Box>
                        {apiResponse && (
                            <IconButton size="small" onClick={() => handleCopy(apiResponse, 'response')}>
                                {copiedStates['response'] ? <CheckIcon fontSize="small" sx={{ color: '#10b981' }} /> : <CopyIcon fontSize="small" />}
                            </IconButton>
                        )}
                    </Box>

                    <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
                        {isLoadingApi && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
                                <CircularProgress size={40} sx={{ color: '#667eea' }} />
                                <Typography variant="body2" color="text.secondary">
                                    Sending request...
                                </Typography>
                            </Box>
                        )}

                        {!isLoadingApi && !apiResponse && !apiError && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8' }}>
                                <CodeIcon sx={{ fontSize: 64, mb: 2, color: '#cbd5e1' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: '#64748b', mb: 1 }}>
                                    No Response Yet
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#94a3b8', textAlign: 'center' }}>
                                    Execute a request to see the response here
                                </Typography>
                            </Box>
                        )}

                        {apiError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {apiError}
                            </Alert>
                        )}

                        {apiResponse && (
                            <Box
                                sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    bgcolor: '#0f172a',
                                    color: '#e2e8f0',
                                    p: 3,
                                    borderRadius: 2,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}
                            >
                                {apiResponse}
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    </Box>
    );
}


